// portalKit: shared pieces for the candidate portal, built on the CRM record
// layout (gtr2). Line icons, dates, the Lexa intent carried from a job page or
// an application, and the "get ready for this interview" card.
// P2-PORTAL (22 Oct 2026)

export const ICONS = {
  pin: 'M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  doc: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h6',
  spark: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
  brief: 'M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M4 7h16a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a1 1 0 0 1 1-1zM3 12h18',
  chat: 'M21 12a8 8 0 0 1-8 8H4l2.5-2.5A8 8 0 1 1 21 12z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-4.3-4.3',
  check: 'M5 12.5l4.5 4.5L19 7',
  cam: 'M3 7h12v10H3zM15 10l6-3v10l-6-3',
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  lock: 'M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4',
  back: 'M15 18l-6-6 6-6',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  route: 'M6 3v12a3 3 0 0 0 3 3h9M15 15l3 3-3 3',
  compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM15.5 8.5l-2 5-5 2 2-5z',
};

export function Ico({ d, cls, size }) {
  return (
    <svg className={'gtr2-ico' + (cls ? ' ' + cls : '')} viewBox="0 0 24 24" width={size || 14} height={size || 14}
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function initials(name) {
  const p = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return 'GT';
  return ((p[0][0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

export function ukDate(v, long) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', long ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

export function workPattern(v) {
  const n = String(v || '').replace(/_/g, '-').toLowerCase();
  return { 'on-site': 'On-site', hybrid: 'Hybrid', remote: 'Remote' }[n] || (v || '');
}

// The Lexa intent: someone clicked "practise for this role" on a job page or
// after applying. It survives sign-up and the welcome screen, so it lives in
// sessionStorage until a session is set up or they choose something else.
const INTENT_KEY = 'gtco_lexa_intent';
const INTENT_TTL = 6 * 60 * 60 * 1000;

export function captureLexaIntent() {
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('lexa') !== 'prep') return;
    const kind = qs.get('kind') === 'career' ? 'career' : 'interview';
    window.sessionStorage.setItem(INTENT_KEY, JSON.stringify({ kind, role: qs.get('role') || '', at: Date.now() }));
  } catch (e) { /* storage blocked: the link still opens the Lexa tab */ }
}

export function readLexaIntent() {
  try {
    const raw = window.sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || Date.now() - (v.at || 0) > INTENT_TTL) { window.sessionStorage.removeItem(INTENT_KEY); return null; }
    return v;
  } catch (e) { return null; }
}

export function setLexaIntent(kind, role) {
  try { window.sessionStorage.setItem(INTENT_KEY, JSON.stringify({ kind: kind || 'interview', role: role || '', at: Date.now() })); } catch (e) { /* fine */ }
}

export function clearLexaIntent() {
  try { window.sessionStorage.removeItem(INTENT_KEY); } catch (e) { /* fine */ }
}

// "Get ready for this interview", shown against a specific role. Sells what
// Lexa does for this job, never the price.
export function LexaPrepCard({ roleTitle, onPractise, onCoach, compact }) {
  return (
    <section className={'gtp2-prep' + (compact ? ' gtp2-prep--compact' : '')}>
      <div className="gtp2-prep-h">
        <span className="gtlx-orb" aria-hidden="true" />
        <div>
          <b>Get ready for this interview</b>
          <span>Practise {roleTitle ? 'the ' + roleTitle + ' interview' : 'this interview'} on video with Lexa, our AI coach. She builds the questions from this role and your CV.</span>
        </div>
      </div>
      {!compact && (
        <ul className="gtp2-prep-list">
          <li><Ico d={ICONS.check} />Coaching after each answer, or a straight run with a debrief at the end</li>
          <li><Ico d={ICONS.check} />A written report on every answer, with a stronger way to put it</li>
          <li><Ico d={ICONS.check} />The questions this role is likely to ask that you have not prepared yet</li>
        </ul>
      )}
      <div className="gtp2-prep-acts">
        <button type="button" className="gtr2-btn gtp2-btn--lexa" onClick={onPractise}>Practise for this role</button>
        {onCoach && <button type="button" className="gtp2-linkbtn" onClick={onCoach}>Or talk through your next move with her</button>}
      </div>
      <p className="gtp2-prep-fine">Optional and separate from your application. It never affects whether we put you forward.</p>
    </section>
  );
}
